import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { InfoIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import AnswersNotice from '@/components/ui/answers-notice';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import {
  getInstitutionProprietor,
  type InstitutionProprietorTaxSelfCertification,
  isDeclaredForProprietorsResidences,
} from '@/features/institution/proprietor-tax-self-certification/schema';
import { InstitutionTaxSelfCertificationFieldsDeclarations } from '@/features/institution/tax-self-certification/components/fieldsets/declaration';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';

const InstitutionProprietorTaxSelfCertificationFieldsetLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.proprietorTaxSelfCertification.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.proprietorTaxSelfCertification.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionProprietorTaxSelfCertificationFieldsetLegend.displayName = 'InstitutionProprietorTaxSelfCertificationFieldsetLegend';

const InstitutionProprietorTaxSelfCertificationFieldset = () => {
  const form = useFormContext<InstitutionProprietorTaxSelfCertification>();
  const { data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { data: countries } = useMainstreamCountries();
  const { t } = useTranslation();
  const proprietor = getInstitutionProprietor(institutionDetailsAnswers?.ownership);
  const proprietorTaxSelfCertification = institutionDetailsAnswers?.proprietorTaxSelfCertification;

  if (!proprietor) return null;

  return (
    <div className="space-y-6">
      {proprietorTaxSelfCertification && !isDeclaredForProprietorsResidences(proprietorTaxSelfCertification, proprietor) ? (
        <Alert variant="info" role="status">
          <InfoIcon aria-hidden className="size-4" />
          <AlertDescription>{t('institutionDetails.proprietorTaxSelfCertification.errors.taxResidencesNotAsOwnership')}</AlertDescription>
        </Alert>
      ) : null}
      <section className="space-y-3">
        <h3 className="font-medium">{t('institutionDetails.proprietorTaxSelfCertification.taxResidences')}</h3>
        <ul className="space-y-2">
          {proprietor.taxResidences.map(({ country, taxId }) => (
            <li key={country} className="flex justify-between gap-4 rounded-md bg-background p-4 text-sm">
              <span>{countries?.[country]?.name ?? country}</span>
              <span className="text-muted-foreground">{taxId ?? t('institutionDetails.proprietorTaxSelfCertification.noTaxId')}</span>
            </li>
          ))}
        </ul>
        <AnswersNotice icon={InfoIcon}>{t('institutionDetails.proprietorTaxSelfCertification.taxResidencesFromOwnership')}</AnswersNotice>
      </section>
      <p className="text-sm">
        <span className="font-medium">{t('institutionDetails.proprietorTaxSelfCertification.signatory')}</span> {proprietor.firstName} {proprietor.lastName}
      </p>
      <FormField
        control={form.control}
        name="signatory.capacity"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.declaration.signatoryCapacity')}*</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.signatoryCapacity')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <InstitutionTaxSelfCertificationFieldsDeclarations context="proprietor" />
    </div>
  );
};
InstitutionProprietorTaxSelfCertificationFieldset.displayName = 'InstitutionProprietorTaxSelfCertificationFieldset';

export { InstitutionProprietorTaxSelfCertificationFieldset, InstitutionProprietorTaxSelfCertificationFieldsetLegend };
